import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "./lib/auth";
import type { Role } from "./lib/types";
import { homeFor } from "./lib/home";
import { AppShell } from "./components/AppShell";
import { SignIn } from "./routes/SignIn";
import { Pay, PayComplete, PayCancelled } from "./routes/Pay";
import { Invite } from "./routes/Invite";
import { Licenses } from "./routes/Licenses";
import { LicenseDetailRoute } from "./routes/LicenseDetail";
import { Orders } from "./routes/Orders";
import { OrderDetailRoute } from "./routes/OrderDetail";
import { Projects } from "./routes/Projects";
import { ProjectDetailRoute } from "./routes/ProjectDetail";
import { Commission } from "./routes/Commission";
import { Marketers } from "./routes/Marketers";
import { MarketerDetailRoute } from "./routes/MarketerDetail";
import { PaymentLinks } from "./routes/PaymentLinks";
import { Dashboard } from "./routes/Dashboard";
import { Overview } from "./routes/Overview";
import { Reports } from "./routes/Reports";
import { Customers } from "./routes/Customers";
import { Blog } from "./routes/Blog";
import { PostEditor } from "./routes/PostEditor";
import { Leads } from "./routes/Leads";
import { Catalogue } from "./routes/Catalogue";
import { SigningKeys } from "./routes/SigningKeys";
import { Accounts } from "./routes/Accounts";
import { AuditLog } from "./routes/AuditLog";
import { Settings } from "./routes/Settings";
import { Register } from "./routes/Register";
import { Plans } from "./routes/Plans";
import { ForgotPassword } from "./routes/ForgotPassword";
import { ResetPassword } from "./routes/ResetPassword";

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<SignIn />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset/:token" element={<ResetPassword />} />

      {/* Public, and outside the shell on purpose. A buyer following a payment
          link has no account here and must not be asked to sign in. */}
      <Route path="/pay/complete" element={<PayComplete />} />
      <Route path="/pay/cancelled" element={<PayCancelled />} />
      <Route path="/pay/:token" element={<Pay />} />
      <Route path="/invite/:token" element={<Invite />} />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route
          path="/overview"
          element={
            <RequireRole roles={["admin", "customer", "marketer"]}>
              <Overview />
            </RequireRole>
          }
        />
        <Route
          path="/dashboard"
          element={
            <RequireRole roles={["admin"]}>
              <Dashboard />
            </RequireRole>
          }
        />
        <Route
          path="/orders"
          element={
            <RequireRole roles={["admin", "customer", "marketer"]}>
              <Orders />
            </RequireRole>
          }
        />
        <Route
          path="/orders/:id"
          element={
            <RequireRole roles={["admin", "customer", "marketer"]}>
              <OrderDetailRoute />
            </RequireRole>
          }
        />
        {/* A customer sees only their own; the API scopes every query, so this
            gate is about not offering a screen that would answer 403. */}
        <Route
          path="/projects"
          element={
            <RequireRole roles={["admin", "customer"]}>
              <Projects />
            </RequireRole>
          }
        />
        <Route
          path="/projects/:id"
          element={
            <RequireRole roles={["admin", "customer"]}>
              <ProjectDetailRoute />
            </RequireRole>
          }
        />
        <Route
          path="/licenses"
          element={
            <RequireCustody roles={["admin", "customer"]}>
              <Licenses />
            </RequireCustody>
          }
        />
        <Route
          path="/licenses/:id"
          element={
            <RequireCustody roles={["admin", "customer"]}>
              <LicenseDetailRoute />
            </RequireCustody>
          }
        />
        <Route
          path="/commission"
          element={
            <RequireRole roles={["admin", "marketer"]}>
              <Commission />
            </RequireRole>
          }
        />
        <Route
          path="/payment-links"
          element={
            <RequireRole roles={["admin", "marketer"]}>
              <PaymentLinks />
            </RequireRole>
          }
        />
        <Route
          path="/reports"
          element={
            <RequireRole roles={["admin"]}>
              <Reports />
            </RequireRole>
          }
        />
        <Route
          path="/marketers"
          element={
            <RequireRole roles={["admin"]}>
              <Marketers />
            </RequireRole>
          }
        />
        <Route
          path="/marketers/:id"
          element={
            <RequireRole roles={["admin"]}>
              <MarketerDetailRoute />
            </RequireRole>
          }
        />
        <Route
          path="/customers"
          element={
            <RequireRole roles={["admin"]}>
              <Customers />
            </RequireRole>
          }
        />
        <Route
          path="/blog"
          element={
            <RequireRole roles={["admin"]}>
              <Blog />
            </RequireRole>
          }
        />
        {/* Its own route rather than an id that happens to read "new": the
            editor is told which mode it is in, instead of inferring it from a
            string a post could in principle be given. */}
        <Route
          path="/blog/new"
          element={
            <RequireRole roles={["admin"]}>
              <PostEditor isNew />
            </RequireRole>
          }
        />
        <Route
          path="/blog/:id"
          element={
            <RequireRole roles={["admin"]}>
              <PostEditor />
            </RequireRole>
          }
        />
        <Route
          path="/leads"
          element={
            <RequireRole roles={["admin"]}>
              <Leads />
            </RequireRole>
          }
        />
        <Route
          path="/catalogue"
          element={
            <RequireRole roles={["admin"]}>
              <Catalogue />
            </RequireRole>
          }
        />
        {/* Owner only. RequireRole cannot express this: an owner reports as an
            admin so that every other admin screen keeps working for it. */}
        <Route
          path="/accounts"
          element={
            <RequireOwner>
              <Accounts />
            </RequireOwner>
          }
        />
        <Route
          path="/signing-keys"
          element={
            <RequireCustody roles={["admin"]}>
              <SigningKeys />
            </RequireCustody>
          }
        />
        <Route
          path="/audit"
          element={
            <RequireCustody roles={["admin"]}>
              <AuditLog />
            </RequireCustody>
          }
        />
        <Route
          path="/plans"
          element={
            <RequireRole roles={["customer"]}>
              <Plans />
            </RequireRole>
          }
        />
        {/* No role gate. Every account has a password and can choose to add a
            second factor, whoever they are. */}
        <Route path="/settings" element={<Settings />} />
      </Route>

      <Route path="*" element={<Home />} />
    </Routes>
  );
}

function Home() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user === null) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user.role)} replace />;
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="flex min-h-dvh items-center justify-center text-sm text-dim">Loading…</div>;
  }
  if (user === null) {
    // Where they were headed survives the sign-in, so a bookmarked licence
    // still opens after the session lapsed.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

/**
 * A second gate in the UI. The server enforces this too — this exists so an
 * account never sees a screen that would only answer with 403.
 */
/**
 * The account screens, which an admin must not reach even by typing the URL.
 * The API refuses them anyway; this is so an admin is not shown a screen that
 * exists only to answer 403.
 */
function RequireOwner({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user.isOwner) return <Navigate to={homeFor(user.role)} replace />;
  return <>{children}</>;
}

/**
 * The custody screens: licences, the keys that sign them, and the audit trail.
 *
 * Hiding them from the sidebar is not enough. Somebody who has been there
 * before has the URL in their history bar, and typing it rendered the whole
 * screen — filters, an Export button — over an empty table, because every
 * request behind it was being refused. That reads as "there is nothing here"
 * rather than "this is not yours", which is a worse answer than either.
 *
 * The API is still the boundary; this stops the interface offering a door that
 * only opens onto a 403.
 */
function RequireCustody({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (!roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  // A customer keeps their own licence screen — `canSeeLicensing` is false only
  // for staff below the owner, which is exactly who this is for.
  if (user.canSeeLicensing === false) return <Navigate to={homeFor(user.role)} replace />;
  return <>{children}</>;
}

function RequireRole({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (!roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return <>{children}</>;
}
